import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  CSSProperties,
} from 'react'
import CustomIcons from './CustomIcons'
import Modal from './Modal'
import UniversalLoader from './UniversalLoader'
import AIEditableText from './ai_editable_container/AIEditableText'
import {
  Container,
  OuterContainer,
  ContentArea,
  GradientContainer,
  ActionContainer,
} from './styles/AIEditableText.styles'
import theme from '../../styles/theme'
import { SurfaceGhostIconButton } from './styles/Button.styles'
import { Message } from 'rsuite' // Add this import
import useStandardToaster from './StandardToaster'
import { generateAIDescription } from '../../utils/generateAIDescription'
import {
  useAIPlusDescription,
  useAIDescription,
  useConclusion,
  useConclusionPlus,
  useKeyInsights,
  useKeyInsightsPlus,
  AITextType,
  type BaseSlice,
} from '../reports/comp_reusable/report_page_universal/stores/baseReportStore'

// Type for animation types
type AnimationType = 'NONE' | 'ANIMATE_NOW' | 'ANIMATE_ON_ENTER_SCREEN'

// Type for a store that implements BaseSlice
type BaseStoreHook<T extends BaseSlice> = (selector: (state: T) => any) => any

// Interface for the modal props
interface AIEditableContainerModalProps {
  title: string
  text: string
  onClose: () => void
  [key: string]: any // Additional props
}

// Interface for the main component props
interface AIEditableContainerProps {
  title: string
  prompt: string
  textSrcType?: AITextType
  hasAIPlus?: boolean // Made optional since textSrcType now determines plus/regular
  plotGroupId?: number // Made optional for report-level text
  plotPackageId?: number // Made optional for report-level text
  loading?: boolean
  isLoading?: boolean
  maxHeight?: number
  style?: CSSProperties
  isNewlyGeneratedReport?: boolean
  wide?: boolean
  store?: BaseStoreHook<any>
}

const AIEditableContainerModal: React.FC<AIEditableContainerModalProps> = ({
  title,
  text,
  onClose,
  ...props
}) => (
  <Modal title={title} onClose={onClose} {...props}>
    <Modal.Body>
      <AIEditableText
        text={text}
        style={{ padding: '2rem' }}
        animateType="NONE"
      />
    </Modal.Body>
  </Modal>
)

const AIEditableContainer: React.FC<AIEditableContainerProps> = ({
  title,
  prompt,
  textSrcType = AITextType.PLOT_DESCRIPTION,
  hasAIPlus,
  plotGroupId,
  plotPackageId,
  loading = false,
  isLoading: externalLoading = false,
  maxHeight = 250,
  style,
  isNewlyGeneratedReport = false,
  wide = false,
  store,
}) => {
  // Access the store functions - use passed store or provide fallback
  const setAIText = store
    ? store((state: BaseSlice) => state.setAIText)
    : () => {}
  const setReportLevelAIText = store
    ? store((state: BaseSlice) => state.setReportLevelAIText)
    : () => {}

  // Create a no-op store for when store is not provided
  const noOpStore = () => null
  const storeToUse = store || noOpStore

  // Get store text based on textSrcType - always call hooks unconditionally
  // When store is null or conditions aren't met, the hooks will return null
  const plotDescriptionText = useAIDescription(
    storeToUse,
    plotGroupId ?? -1,
    plotPackageId ?? -1,
  )
  const plotDescriptionPlusText = useAIPlusDescription(
    storeToUse,
    plotGroupId ?? -1,
    plotPackageId ?? -1,
  )
  const conclusionText = useConclusion(storeToUse)
  const conclusionPlusText = useConclusionPlus(storeToUse)
  const keyInsightsText = useKeyInsights(storeToUse)
  const keyInsightsPlusText = useKeyInsightsPlus(storeToUse)

  // Apply conditional logic after hooks are called
  const validPlotDescriptionText =
    store && plotGroupId !== undefined && plotPackageId !== undefined
      ? plotDescriptionText
      : null
  const validPlotDescriptionPlusText =
    store && plotGroupId !== undefined && plotPackageId !== undefined
      ? plotDescriptionPlusText
      : null
  const validConclusionText = store ? conclusionText : null
  const validConclusionPlusText = store ? conclusionPlusText : null
  const validKeyInsightsText = store ? keyInsightsText : null
  const validKeyInsightsPlusText = store ? keyInsightsPlusText : null

  // Determine the store text based on textSrcType
  const getStoreText = (): string | null => {
    switch (textSrcType) {
      case AITextType.PLOT_DESCRIPTION:
        return validPlotDescriptionText
      case AITextType.PLOT_DESCRIPTION_PLUS:
        return validPlotDescriptionPlusText
      case AITextType.CONCLUSION:
        return validConclusionText
      case AITextType.CONCLUSION_PLUS:
        return validConclusionPlusText
      case AITextType.KEY_INSIGHTS:
        return validKeyInsightsText
      case AITextType.KEY_INSIGHTS_PLUS:
        return validKeyInsightsPlusText
      default:
        // Fallback to legacy behavior for backward compatibility
        return hasAIPlus &&
          plotGroupId !== undefined &&
          plotPackageId !== undefined
          ? validPlotDescriptionPlusText
          : validPlotDescriptionText
    }
  }

  const storeText = getStoreText()

  const [showModal, setShowModal] = useState<boolean>(false)
  const [, setIsOverflowing] = useState<boolean>(false)
  const [isScrollMode, setIsScrollMode] = useState<boolean>(false)
  const [isAtTopScroll, setIsAtTopScroll] = useState<boolean>(true)
  const [isTextOverflowing, setIsTextOverflowing] = useState<boolean>(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollTimerRef = useRef<NodeJS.Timeout | null>(null)
  const [, setInitPaddingRightContainer] = useState<number | null>(null)
  const [isEditable, setIsEditable] = useState<boolean>(false)
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false)
  const [isAnimating, setIsAnimating] = useState<boolean>(false)
  const textareaRef = useRef<HTMLTextAreaElement | HTMLDivElement | null>(null)
  const [hasUserEnabledEditing, setHasUserEnabledEditing] =
    useState<boolean>(false)
  const [editedText, setEditedText] = useState<string>('')

  const hasEverBeenAnimated = useRef<boolean>(false) // Track if content has ever been animated

  const [animationType, setAnimationType] = useState<AnimationType>(
    isNewlyGeneratedReport && !hasEverBeenAnimated.current
      ? 'ANIMATE_ON_ENTER_SCREEN'
      : 'NONE',
  )

  // Always use storeText as source of truth - let AIEditableText manage its own editing state
  const displayText =
    storeText || 'No generated text. Click refresh to generate new text.'

  const isLoading = loading || externalLoading || isRefreshing
  const { pushToast } = useStandardToaster()

  const syncUpstreamToStore = (text: string) => {
    // Handle report-level text types
    if (
      textSrcType === AITextType.CONCLUSION ||
      textSrcType === AITextType.CONCLUSION_PLUS ||
      textSrcType === AITextType.KEY_INSIGHTS ||
      textSrcType === AITextType.KEY_INSIGHTS_PLUS
    ) {
      setReportLevelAIText({
        textSrcType: textSrcType,
        text: text,
      })
    } else {
      // Handle plot-level text types (backward compatibility and new plot types)
      if (plotGroupId !== undefined && plotPackageId !== undefined) {
        const isPlus =
          textSrcType === AITextType.PLOT_DESCRIPTION_PLUS ||
          (hasAIPlus === true &&
            (textSrcType === AITextType.PLOT_DESCRIPTION || !textSrcType))
        setAIText({
          plotGroupId: plotGroupId,
          plotPackageId: plotPackageId,
          text: text,
          hasPlus: isPlus,
        })
      } else {
        console.warn(
          'plotGroupId and plotPackageId are required for plot-level text updates',
        )
      }
    }
  }

  // Simplified refresh function (replaces fetchText('refresh'))
  const refreshText = async (): Promise<void> => {
    if (isRefreshing) return
    setIsRefreshing(true)

    try {
      const newText = await generateAIDescription(title, prompt, false)

      // Update store (which will trigger the effect above)
      syncUpstreamToStore(newText)
      setAnimationType('ANIMATE_NOW')
    } catch (error) {
      console.error('Failed to generate new text:', error)
      pushToast(<Message type="error">Failed to generate new text</Message>, {
        duration: 2000,
      })
    } finally {
      setIsRefreshing(false)
    }
  }

  // Handle overflow change from AIEditableText
  const handleOverflowChange = useCallback(
    (isOverflowing: boolean) => {
      setIsTextOverflowing(isOverflowing)

      // Show gradient only if overflowing and not in scroll mode and not editing
      setIsOverflowing(isOverflowing && !isScrollMode && !isEditable)
    },
    [isScrollMode, isEditable],
  )

  // Handle scroll position change from AIEditableText
  const handleScrollPositionChange = useCallback(
    (scrollTop: number, maxScroll: number, isAtTop: boolean) => {
      setIsAtTopScroll(isAtTop)

      // Handle timer based on scroll position
      if (isAtTop && isScrollMode && !isEditable) {
        resetScrollTimer()
      } else if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current)
      }
    },
    /* eslint-disable-next-line */
    [isScrollMode, isEditable],
  )

  const resetScrollTimer = useCallback(() => {
    if (scrollTimerRef.current) {
      clearTimeout(scrollTimerRef.current)
    }

    // Only start timer if we're not editing, in scroll mode, and content is overflowing
    if (!isEditable && isScrollMode && isTextOverflowing) {
      scrollTimerRef.current = setTimeout(() => {
        setIsScrollMode(false)
      }, 2000)
    }
  }, [isEditable, isScrollMode, isTextOverflowing]) // Remove checkOverflow function since AIEditableText handles overflow detection
  // and reports it via onOverflowChange callback

  useEffect(() => {
    if (containerRef.current) {
      const paddingRight = window.getComputedStyle(
        containerRef.current,
      ).paddingRight
      setInitPaddingRightContainer(parseFloat(paddingRight))
    }
  }, [])

  const handleChevronClick = () => {
    setIsScrollMode(true)
    setIsOverflowing(false)
  }

  const hasStartedAnimating = useRef<boolean>(false)

  const handleAnimatingChange = useCallback((animating: boolean) => {
    setIsAnimating(animating)

    if (animating && !hasStartedAnimating.current) {
      hasStartedAnimating.current = true
    }

    // Track that content has been animated at least once
    // We can track this without checking animationType since any animation is meaningful
    if (animating) {
      hasEverBeenAnimated.current = true
    }
  }, []) // No dependencies needed

  const canEdit = !isLoading && !isAnimating && displayText

  const handleEditToggle = () => {
    if (canEdit) {
      setIsEditable(!isEditable)
      setHasUserEnabledEditing(true)

      setTimeout(() => {
        if (textareaRef.current) {
          ;(textareaRef.current as HTMLTextAreaElement).focus?.()
        }
      }, 1)
    }
  }
  const handleTextareaRef = useCallback(
    (ref: HTMLTextAreaElement | HTMLDivElement | null) => {
      textareaRef.current = ref
    },
    [],
  )

  const scrollToTop = useCallback(() => {
    // Scroll the text container to top
    if (textareaRef.current) {
      textareaRef.current.scrollTop = 0
    }
  }, [])

  const handleRefreshBtn = async (): Promise<void> => {
    await refreshText()
  }

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(displayText)
      pushToast(
        <Message type="success">
          {`Copied text: ${displayText.slice(0, 50)}${displayText.length > 50 ? '...' : ''}`}
        </Message>,
        { duration: 2000 },
      )
    } catch (err) {
      console.error('Failed to copy:', err)
      pushToast(
        <Message type="error">Failed to copy text to clipboard</Message>,
        { duration: 2000 },
      )
    }
  }

  const handleUserFinishedEditingText = async (): Promise<void> => {
    try {
      // Update the store using setAIText
      syncUpstreamToStore(editedText)

      setHasUserEnabledEditing(false)

      // Note: isEditable will be set to false in handleBlur
    } catch (error) {
      console.error('Something went wrong with editing text:', error)
      pushToast(
        <Message type="error">Something went wrong with editing text</Message>,
        { duration: 2000 },
      )
    }
  }

  const handleTextChange = (newText: string): void => {
    setEditedText(newText)
    // onTextChange?.(newText); // Call parent's onTextChange when text changes
  }

  const handleBlur = (): void => {
    if (isEditable) {
      handleUserFinishedEditingText()
      // Exit edit mode and scroll to top
      setIsEditable(false)
      scrollToTop()
    }
  } // Add effect to clean up scroll mode after editing

  useEffect(() => {
    if (!isEditable && isScrollMode) {
      // When exiting edit mode, check if we should start the timer
      if (isAtTopScroll) {
        resetScrollTimer()
      }
    }
  }, [isEditable, isScrollMode, isAtTopScroll, resetScrollTimer])

  // Handle exiting edit mode - scroll to top and reset scroll state
  useEffect(() => {
    if (!isEditable && hasUserEnabledEditing) {
      // Scroll to top when exiting edit mode
      scrollToTop()

      // Reset scroll mode and start timer if content overflows
      if (isTextOverflowing) {
        setIsScrollMode(false)
        // Since we're at top after scrolling, start the timer
        resetScrollTimer()
      }
    }
  }, [
    isEditable,
    hasUserEnabledEditing,
    scrollToTop,
    isTextOverflowing,
    resetScrollTimer,
  ]) // Update button disabled states based on loading OR animation
  const buttonsDisabled = isLoading || isAnimating
  return (
    <div
      style={{
        padding: wide ? '0rem 1.5rem 0.5rem 0.7rem' : '1rem 1rem 1rem 1rem',
      }}
    >
      <OuterContainer
        style={{
          ...style,
        }}
      >
        {' '}
        <Container>
          <ContentArea
            ref={containerRef}
            style={{
              overflowY: 'hidden', // No more scroll on container
              transition: 'all 0.3s ease',
              minHeight: isLoading ? '100px' : '1rem',
            }}
          >
            <UniversalLoader show={isLoading} />

            <AIEditableText
              isScrollMode={isScrollMode || isEditable} // Make sure this prop is passed
              text={displayText}
              isLoading={isLoading}
              isEditable={isEditable}
              onAnimatingChange={handleAnimatingChange}
              onRef={handleTextareaRef}
              onChange={handleTextChange}
              onBlur={handleBlur}
              animateType={animationType}
              onOverflowChange={handleOverflowChange}
              onScrollPositionChange={handleScrollPositionChange}
              maxHeight={maxHeight}
            />

            <GradientContainer
              show={isTextOverflowing && !isScrollMode && !isEditable}
            >
              <SurfaceGhostIconButton onClick={handleChevronClick}>
                <CustomIcons.ChevronDownIcon
                  size="xxs"
                  color={theme.colors.veryVeryFadedText}
                />
              </SurfaceGhostIconButton>
            </GradientContainer>
          </ContentArea>
        </Container>{' '}
        <ActionContainer>
          <CustomIcons.PenIcon
            color={
              isEditable
                ? theme.colors.standardText
                : theme.colors.veryVeryFadedText
            }
            size="xs"
            onClick={handleEditToggle}
            disabled={buttonsDisabled}
          />

          <CustomIcons.ProcessIcon
            color={theme.colors.veryVeryFadedText}
            size="xs"
            onClick={handleRefreshBtn}
            disabled={buttonsDisabled}
          />

          <CustomIcons.CopyIcon
            color={theme.colors.veryVeryFadedText}
            size="xs"
            onClick={handleCopy}
            disabled={buttonsDisabled}
          />
        </ActionContainer>
      </OuterContainer>
      <AIEditableContainerModal
        title={title}
        text={displayText}
        open={showModal}
        onClose={() => setShowModal(false)}
        animateType="none"
      />
    </div>
  )
}

export default AIEditableContainer
